import { useState, useEffect } from "react";
import { useWallet } from "../hooks/useWallet";
import { WalletProviderChooser } from "../components/WalletProviderChooser";
import { useApi } from "../hooks/useApi";
import { ADDRESS_ENV, EXPECTED_CHAIN, EXPECTED_CHAIN_LABEL, envAddress, requireEnvAddress } from "../utils/config";
import { CODE_APE_HOOK_ABI } from "../utils/contracts";

interface NftWithFees {
  tokenId: number;
  pendingWethFeesWei: string;
  active: boolean;
}

export default function FeeClaimPage() {
  const { account, walletClient, connect, switchToExpectedChain, chainId, isConnecting, error: walletError, walletOptions, selectedWalletId, selectWallet } = useWallet();
  const hookConfigured = Boolean(envAddress(ADDRESS_ENV.hook));
  const { get } = useApi();
  const [nfts, setNfts] = useState<NftWithFees[]>([]);
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState<number | null>(null);
  const [claimingAll, setClaimingAll] = useState(false);
  const [claimAsNativeEth, setClaimAsNativeEth] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successTokenId, setSuccessTokenId] = useState<number | null>(null);

  useEffect(() => {
    if (account) loadMyNfts();
  }, [account]);

  async function loadMyNfts() {
    if (!account) return;
    setLoading(true); setError(null);
    try {
      const data = await get<NftWithFees[]>(`/indexer/nfts?owner=${account}`);
      setNfts(data.filter((n) => n.active));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load NFTs");
    } finally {
      setLoading(false);
    }
  }

  async function submitClaim(tokenId: number) {
    if (!walletClient || !account) return;
    const functionName = claimAsNativeEth ? "claimEthFeesAsNative" : "claimWethFees";
    const args = claimAsNativeEth ? [BigInt(tokenId), account] as const : [BigInt(tokenId)] as const;
    const tx = await walletClient.writeContract({
      address: requireEnvAddress(ADDRESS_ENV.hook),
      abi: CODE_APE_HOOK_ABI,
      functionName,
      args,
      account,
      chain: EXPECTED_CHAIN
    });
    void tx;
  }

  async function claimFees(tokenId: number) {
    setClaiming(tokenId); setError(null); setSuccessTokenId(null);
    try {
      await submitClaim(tokenId);
      setSuccessTokenId(tokenId);
      await loadMyNfts();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Claim failed");
    } finally {
      setClaiming(null);
    }
  }

  async function claimAllFeesSequentially() {
    const claimable = nfts.filter((nft) => BigInt(nft.pendingWethFeesWei || "0") > 0n);
    if (!walletClient || !account || claimable.length === 0) return;
    const confirmed = window.confirm(`Claim fees for ${claimable.length} NFT${claimable.length === 1 ? "" : "s"} as ${claimAsNativeEth ? "native ETH" : "WETH"}? This will submit one wallet transaction per token.`);
    if (!confirmed) return;
    setClaimingAll(true); setError(null); setSuccessTokenId(null);
    try {
      for (const nft of claimable) {
        setClaiming(nft.tokenId);
        await submitClaim(nft.tokenId);
      }
      await loadMyNfts();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Claim-all failed");
    } finally {
      setClaiming(null);
      setClaimingAll(false);
    }
  }

  const totalPendingWei = nfts.reduce((acc, n) => acc + BigInt(n.pendingWethFeesWei || "0"), 0n);
  const claimableCount = nfts.filter((n) => BigInt(n.pendingWethFeesWei || "0") > 0n).length;
  const isExpectedChain = chainId === EXPECTED_CHAIN.id;

  return (
    <main className="page">
      <div className="container" style={{ maxWidth: 860, paddingBottom: "var(--space-24)" }}>
        <div style={{ marginBottom: "var(--space-8)" }}>
          <span className="eyebrow">// stakeless fee stream · receipt, not key</span>
          <h1 className="text-4xl" style={{ marginBottom: "var(--space-3)" }}>
            WETH <span className="gradient-text">Fee Claims</span>
          </h1>
          <p className="text-muted">
            You did not stake. You did not sign a vault contract. You held an NFT. While you held it, the Uniswap v4 hook routed official WETH fee flow to your share of the active set. This page lets you collect whatever has accrued so far.
          </p>
          <p className="lede-alt">
            Hook is the cashier. Hold, not stake. Fees only flow when trading happens; this is not advertised APY.
          </p>
        </div>

        {/* Pending total */}
        {account && (
          <div className="card" style={{ marginBottom: "var(--space-6)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "var(--space-4)" }}>
            <div>
              <div className="text-sm text-muted" style={{ marginBottom: 4 }}>Total Pending WETH</div>
              <div className="text-3xl" style={{ color: "var(--color-weth)", fontFamily: "var(--text-mono)" }}>
                {formatWeth(totalPendingWei)} WETH
              </div>
            </div>
            <div style={{ display: "flex", gap: "var(--space-3)", alignItems: "center", flexWrap: "wrap" }}>
              <div className="badge badge-cyan">⚡ {nfts.length} active NFT{nfts.length === 1 ? "" : "s"}</div>
              <label className="badge" style={{ cursor: "pointer", borderColor: "var(--color-border)", color: "var(--text-secondary)" }}>
                <input
                  type="checkbox"
                  checked={claimAsNativeEth}
                  onChange={(event) => setClaimAsNativeEth(event.target.checked)}
                  style={{ marginRight: 6, accentColor: "var(--color-weth)" }}
                />
                Receive native ETH
              </label>
              <button
                className="btn btn-cyan"
                onClick={claimAllFeesSequentially}
                disabled={!hookConfigured || !isExpectedChain || claimingAll || claimableCount === 0}
                id="btn-claim-all-fees"
              >
                {claimingAll ? <><span className="spinner" /> Claiming {claiming ? `#${claiming}` : "all"}…</> : `Claim all ${claimAsNativeEth ? "as ETH" : "as WETH"} (${claimableCount})`}
              </button>
            </div>
          </div>
        )}

        {account && !isExpectedChain && (
          <div className="alert alert-warning" style={{ marginBottom: "var(--space-6)", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
            <div>
              <strong>Wrong network</strong>
              <div className="text-sm text-muted">Switch to {EXPECTED_CHAIN_LABEL} before claiming WETH fees.</div>
            </div>
            <button className="btn btn-secondary" onClick={switchToExpectedChain}>Switch to {EXPECTED_CHAIN_LABEL}</button>
          </div>
        )}

        {!hookConfigured && (
          <div className="alert alert-error" style={{ marginBottom: "var(--space-6)" }}>
            Contract writes disabled: {ADDRESS_ENV.hook} is not configured.
          </div>
        )}

        {!account && (
          <div className="alert alert-info wallet-connect-panel" style={{ marginBottom: "var(--space-6)" }}>
            <div>
              <strong>Connect your wallet</strong>
              <div className="text-sm text-muted">Open this page inside a mobile wallet browser if you are on a phone.</div>
            </div>
            <div className="wallet-connect-panel__actions">
              <WalletProviderChooser walletOptions={walletOptions} selectedWalletId={selectedWalletId} selectWallet={selectWallet} />
              <button className="btn btn-secondary" onClick={() => void connect()} disabled={isConnecting}>
                {isConnecting ? "Connecting…" : "Connect Wallet"}
              </button>
            </div>
          </div>
        )}

        {walletError && <div className="alert alert-error" style={{ marginBottom: "var(--space-4)" }}>{walletError}</div>}

        {error && <div className="alert alert-error" style={{ marginBottom: "var(--space-4)" }}>{error}</div>}
        {successTokenId && (
          <div className="alert alert-success" style={{ marginBottom: "var(--space-4)" }}>
            ✅ Successfully claimed {claimAsNativeEth ? "native ETH" : "WETH"} fees for CODE APE #{successTokenId}
          </div>
        )}

        {loading && (
          <div style={{ display: "flex", justifyContent: "center", padding: "var(--space-12)" }}>
            <span className="spinner spinner-lg" />
          </div>
        )}

        {!loading && nfts.length === 0 && account && (
          <div className="card text-center" style={{ padding: "var(--space-16)" }}>
            <div style={{ fontSize: "3rem", marginBottom: "var(--space-4)" }}>💸</div>
            <p className="text-xl text-muted">No active apes found in this wallet.</p>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
          {nfts.map((nft) => (
            <div key={nft.tokenId} className="card" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-4)", flexWrap: "wrap" }} id={`fee-row-${nft.tokenId}`}>
              <div>
                <div className="text-sm text-muted" style={{ marginBottom: 4 }}>CODE APE #{nft.tokenId}</div>
                <div style={{ fontFamily: "var(--text-mono)", color: "var(--color-weth)", fontSize: "var(--font-xl)" }}>
                  {formatWeth(BigInt(nft.pendingWethFeesWei || "0"))} WETH
                </div>
              </div>
              <button
                id={`btn-claim-fees-${nft.tokenId}`}
                className="btn btn-cyan"
                onClick={() => claimFees(nft.tokenId)}
                disabled={!hookConfigured || !isExpectedChain || claimingAll || claiming === nft.tokenId || BigInt(nft.pendingWethFeesWei || "0") === 0n}
              >
                {claiming === nft.tokenId ? <><span className="spinner" /> Claiming…</> : claimAsNativeEth ? "⚡ Claim ETH" : "⚡ Claim WETH"}
              </button>
            </div>
          ))}
        </div>

        {/* Fee ownership note */}
        <div className="alert alert-warning" style={{ marginTop: "var(--space-8)" }}>
          <span>⚠️</span>
          <div>
            <strong>Fee ownership note:</strong> Pending WETH fees are attached to each NFT, not the wallet.
            This page aggregates your wallet's active NFTs for convenience, but claims still execute per tokenId.
            If you sell your CODE APE with unclaimed fees, the buyer may claim them. Check pending fees before listing on any marketplace.
          </div>
        </div>
      </div>
    </main>
  );
}

function formatWeth(wei: bigint): string {
  const eth = Number(wei * 1_000_000n / (10n ** 18n)) / 1_000_000;
  return eth.toFixed(6);
}
